/**
 * Calculate time-based freshness for device images
 * Returns display text and color variant based on how old the image is
 */
export const getImageFreshness = (lastUpdated: string | null | undefined): {
  text: string;
  variant: 'default' | 'secondary' | 'outline';
  colorClass: string;
} => {
  if (!lastUpdated) {
    return {
      text: 'Unknown',
      variant: 'secondary',
      colorClass: 'bg-gray-500'
    };
  }

  const lastUpdatedDate = new Date(lastUpdated);
  if (isNaN(lastUpdatedDate.getTime())) {
    return {
      text: 'Unknown',
      variant: 'secondary',
      colorClass: 'bg-gray-500'
    };
  }

  const now = Date.now();
  const diffMs = now - lastUpdatedDate.getTime();
  const diffMinutes = diffMs / (1000 * 60);
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Less than 15 minutes - extremely fresh (brightest green)
  if (diffMinutes < 15) {
    return {
      text: '< 15 minutes',
      variant: 'default',
      colorClass: 'bg-green-400 hover:bg-green-500'
    };
  }
  
  // Less than 30 minutes - very very fresh (bright green)
  if (diffMinutes < 30) {
    return {
      text: '< 30 minutes',
      variant: 'default',
      colorClass: 'bg-green-500 hover:bg-green-600'
    };
  }

  // Less than 1 hour - very fresh (green)
  if (diffHours < 1) {
    return {
      text: '< 1 hour',
      variant: 'default',
      colorClass: 'bg-green-500 hover:bg-green-600'
    };
  }
  
  // Less than 2 hours - fresh (green)
  if (diffHours < 2) {
    return {
      text: '< 2 hours',
      variant: 'default',
      colorClass: 'bg-green-500 hover:bg-green-600'
    };
  }
  
  // Less than 4 hours - recent (light green)
  if (diffHours < 4) {
    return {
      text: '< 4 hours',
      variant: 'default',
      colorClass: 'bg-green-600 hover:bg-green-700'
    };
  }
  
  // Less than 8 hours - moderate (yellow-green)
  if (diffHours < 8) {
    return {
      text: '< 8 hours',
      variant: 'default',
      colorClass: 'bg-yellow-500 hover:bg-yellow-600'
    };
  }
  
  // Less than 16 hours - aging (yellow)
  if (diffHours < 16) {
    return {
      text: '< 16 hours',
      variant: 'default',
      colorClass: 'bg-yellow-600 hover:bg-yellow-700'
    };
  }
  
  // Less than 24 hours - old (orange)
  if (diffHours < 24) {
    return {
      text: '< 24 hours',
      variant: 'secondary',
      colorClass: 'bg-orange-500 hover:bg-orange-600'
    };
  }
  
  // More than 24 hours - show days (gray)
  if (diffDays === 1) {
    return {
      text: '< 1 day',
      variant: 'secondary',
      colorClass: 'bg-gray-500'
    };
  }
  
  return {
    text: `< ${diffDays} days`,
    variant: 'secondary',
    colorClass: 'bg-gray-600'
  };
};
